
import { Component, Prop, Mixins, Inject, Watch } from 'vue-property-decorator';
import { IVoucher, voucherHelper } from '@homeserve/vue-api-plugin';
import { State } from 'vuex-class';
import { I18nMixin } from '../../mixins';
import { voucherValidity, isVoucherValid } from '../../utils/voucherHelper';
import { isParameterizedTranslation } from '../../utils/typeGuard';
import { IVoucherInputDefaultI18n, IVoucherInputOverrideI18n } from './types';
import { Validator } from 'vee-validate';

@Component({ $_veeValidate: { validator: 'new' } })
export default class WLVoucherInput extends Mixins(I18nMixin) {
  @Inject('$validator')
  public $validator!: Validator;

  public defaultI18n!: IVoucherInputDefaultI18n;

  @Prop()
  public overrideI18n!: IVoucherInputOverrideI18n;
  private loading = false;
  private voucherInput = '';

  @State('voucher', { namespace: 'api/voucher/voucher' })
  private voucherStore!: IVoucher;

  @State('errorFetchingVoucher', { namespace: 'api/voucher/voucher' })
  private errorFetchingVoucher!: any;

  @Prop()
  private productPrice!: number;

  @Prop()
  private voucher!: IVoucher;

  @Prop()
  private error!: boolean;

  @Prop({ default: false })
  private isPageBankImprintMobile!: boolean;

  private showInput = false;

  private async submit() {
    if (!(await this.$validator.validate(this.translations.discount_code.toString()))) {
      return;
    }

    if (this.voucherInput === '') {
      return this.$validator.errors.add({
        field: this.translations.discount_code.toString(),
        msg: this.translations.please_enter_a_valid_promo_code.toString(),
      });
    }

    this.loading = true;
    await voucherHelper.fetchVoucherWithCode(this.$store, this.voucherInput);
    this.loading = false;

    if (this.errorFetchingVoucher || !this.voucherStore) {
      return this.$validator.errors.add({
        field: this.translations.discount_code.toString(),
        msg: this.translations.this_discount_code_is_not_valid.toString(),
      });
    }
    if (!isVoucherValid(this.voucherStore, this.productPrice)) {
      return this.checkVoucherValidity(this.voucherStore.minCartAmount);
    } else {
      this.$emit('change', this.voucherStore);
    }
  }

  @Watch('error')
  private watchError(error: boolean) {
    if (error) {
      this.$validator.errors.add({
        field: this.translations.discount_code.toString(),
        msg: this.translations.this_discount_code_is_not_valid.toString(),
      });
    }
  }

  private checkVoucherValidity(minAmount: number) {
    const varVoucherValidity = voucherValidity(this.voucherStore, this.productPrice);
    if (varVoucherValidity !== 'valid') {
      let msg = '';
      switch (varVoucherValidity) {
        case 'expired':
          msg = this.translations.this_discount_code_is_expired.toString();
          break;
        case 'min_amount_not_reached':
          if (isParameterizedTranslation(this.translations.this_discount_code_does_not_reach_minimum_amount)) {
            msg = this.translations
              .this_discount_code_does_not_reach_minimum_amount(this.$options.filters!.formatPrice(minAmount))
              .toString();
          }

          break;
        case 'already_used':
          msg = this.translations.this_discount_code_has_already_been_used.toString();
          break;
      }
      this.$validator.errors.add({
        field: this.translations.discount_code.toString(),
        msg,
      });
    }
  }

  created() {
    if (this.voucher && this.voucher.code) {
      this.voucherInput = this.voucher.code;
    }
    if (this.voucherStore) {
      this.voucherInput = this.voucherStore.code;
    }

    this.defaultI18n = {
      i_have_a_discount_code: this.$t('components_plugin.voucher_input.i_have_a_discount_code'),
      fill_your_code: this.$t('components_plugin.voucher_input.fill_your_code'),
      validate: this.$t('components_plugin.voucher_input.validate'),
      this_discount_code_is_not_valid: this.$t('components_plugin.voucher_input.this_discount_code_is_not_valid'),
      please_enter_a_valid_promo_code: this.$t('components_plugin.voucher_input.please_enter_a_valid_promo_code'),
      discount_code: this.$t('components_plugin.voucher_input.discount_code').toString().toLowerCase(),
      this_discount_code_is_expired: this.$t('components_plugin.voucher_input.this_discount_code_is_expired'),
      this_discount_code_does_not_reach_minimum_amount: (minAmount: number) =>
        this.$t('components_plugin.voucher_input.this_discount_code_does_not_reach_minimum_amount', {
          amount: minAmount,
        }),
      this_discount_code_has_already_been_used: this.$t(
        'components_plugin.voucher_input.this_discount_code_has_already_been_used',
      ),
    };
    this.checkVoucherValidity(
      this.voucher ? this.voucher.minCartAmount : this.voucherStore ? this.voucherStore.minCartAmount : 0,
    );
  }

  private inputUpdate() {
    this.$validator.errors.clear();
    if (this.error) {
      this.$emit('update:error', false);
    }
  }
}
